import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from './contexts/AuthContext';
import LandingPage from './components/layout/LandingPage';
import ProjectLoader from './components/threatcat/ProjectLoader';
import MainLayout from './components/layout/MainLayout';
import TopBar from './components/layout/TopBar';
import Callback from './pages/auth/Callback';
import Login from './pages/auth/Login';
import AccessDenied from './pages/auth/AccessDenied';
import Tokens from './pages/tokens/Tokens';
import { Toaster } from '@/components/ui/sonner';
import { PageSkeleton } from '@/components/ui/page-skeleton';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageSkeleton />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function LocalDiagramPage() {
  const { currentDiagram } = useApp();
  const { projectId, clearProject } = useProject();

  useEffect(() => {
    if (projectId !== null) {
      clearProject();
    }
  }, [projectId, clearProject]);

  if (!currentDiagram) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <MainLayout />
    </div>
  );
}

function TokensPage() {
  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <Tokens />
    </div>
  );
}

function HomePage() {
  const { projectId, clearProject } = useProject();

  useEffect(() => {
    if (projectId !== null) {
      clearProject();
    }
  }, [projectId, clearProject]);

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <LandingPage />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/+$/, '')}>
      <Routes>
        <Route path="/auth/callback" element={<Callback />} />
        <Route path="/login" element={<Login />} />
        <Route path="/access-denied" element={<AccessDenied />} />
        <Route path="/tokens" element={<RequireAuth><TokensPage /></RequireAuth>} />
        <Route path="/projects/:projectId" element={<RequireAuth><ProjectLoader /></RequireAuth>} />
        <Route path="/projects" element={<RequireAuth><HomePage /></RequireAuth>} />
        <Route path="/local" element={<RequireAuth><LocalDiagramPage /></RequireAuth>} />
        <Route path="/" element={<RequireAuth><HomePage /></RequireAuth>} />
      </Routes>
      <Toaster richColors position="bottom-right" />
    </BrowserRouter>
  );
}

export default App;
